import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OdlInputEvent } from '@odilo/ui-components';
import { HighContrastMode } from '../../../helpers/high-contrast';
import {
  applyDocumentTheme,
  findReaderThemeByBackgroundColor,
  READER_THEMES,
} from '../../../helpers/reader-themes';
import { FeatureFlagsService } from '../../../services/feature-flags/feature-flags';
import { MetadataService } from '../../../services/metadata/metadata';
import { ReaderNavigatorService } from '../../../services/readium/navigator/reader-navigator';
import { SettingsService } from '../../../services/readium/settings/settings';
import { RangeSliderComponent } from '../../range-slider/range-slider';
import { ThemeSelectorComponent } from '../theme-selector/theme-selector';

@Component({
  selector: 'nr-page-settings',
  imports: [TranslatePipe, ThemeSelectorComponent, RangeSliderComponent, ReactiveFormsModule],
  templateUrl: './page-settings.html',
  styleUrl: './page-settings.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class PageSettingsComponent {
  private static readonly DEFAULT_ZOOM = 1;

  private readonly settingsService = inject(SettingsService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly navigatorService = inject(ReaderNavigatorService);
  private readonly metadataService = inject(MetadataService);

  readonly fullSettingsEnabled = this.featureFlagsService.isEnabled('fullSettings');
  readonly isFixedLayout = this.metadataService.isFixedLayout;
  readonly showColumnOptions = computed(() => this.navigatorService.canUseTwoColumns());
  readonly sliderZoom = new FormControl(PageSettingsComponent.DEFAULT_ZOOM);

  readonly themes = signal(READER_THEMES).asReadonly();

  readonly settings = this.settingsService.settings;
  readonly pageColumns = computed(() => this.settings()?.columnCount?.toString());
  readonly backgroundColor = computed(() => this.settings()?.backgroundColor);
  readonly backgroundColorId = computed(() => {
    if (this.settingsService.highContrastMode() !== HighContrastMode.NONE) return undefined;
    return findReaderThemeByBackgroundColor(this.backgroundColor())?.id;
  });

  constructor() {
    effect(() => {
      this.sliderZoom.setValue(this.navigatorService.fxlZoom(), { emitEvent: false });
    });
    this.sliderZoom.valueChanges.subscribe((val) => {
      this.navigatorService.setFxlZoom(Number(val));
    });
    effect(() => {
      const themeId = this.backgroundColorId();
      if (!themeId) return;
      void applyDocumentTheme(themeId).then(() => {
        if (this.settings()?.linkColor) return;
        const fromCss = getComputedStyle(document.documentElement)
          .getPropertyValue('--link-initial-color')
          .trim();
        const theme = this.themes().find(({ id }) => id === themeId);
        if (!theme) return;
        void this.settingsService.updateReaderSettings({ linkColor: fromCss || theme.linkColor });
      });
    });
  }

  changePageColumns(event: OdlInputEvent): void {
    this.settingsService.updateReaderSettings({
      columnCount: Number(event.detail.value),
    });
    this.navigatorService.setFxlZoom(PageSettingsComponent.DEFAULT_ZOOM);
  }

  changeTheme(event: OdlInputEvent): void {
    const { value } = event.detail;
    const theme = this.themes().find(({ id }) => id === value);
    if (!theme) return;
    this.settingsService.discardHighContrastMode();
    void this.settingsService.updateReaderSettings({
      backgroundColor: theme.backgroundColor,
      textColor: theme.textColor,
    });
    void applyDocumentTheme(theme.id).then(() => {
      const fromCss = getComputedStyle(document.documentElement)
        .getPropertyValue('--link-initial-color')
        .trim();
      void this.settingsService.updateReaderSettings({ linkColor: fromCss || theme.linkColor });
    });
  }
}
